// import { Icon } from "@/components";
import React, {
  FC,
  ButtonHTMLAttributes,
  useState,
  useRef,
  useEffect,
} from "react";
import clsx from "clsx";
import ReactTooltip from "react-tooltip";
import { Icon } from "@/components/Icons";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "lg" | "md" | "sm" | "xs";
  rounded?: boolean;
  outline?: boolean;
  variant?: "default" | "outline" | "link";
  color?: "red" | "blue" | "gray" | "green" | "yellow" | "transparent";
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
  iconType?: "stroke" | "fill";
  iconLeft?: React.ElementType;
  iconRight?: React.ElementType;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  id?: string;
  splitDropdown?: boolean;
  dropdownItems?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ElementType;
    tooltip?: string;
    disabled?: boolean;
  }>;
}

const Button: FC<Props> = ({
  size = "lg",
  rounded = false,
  variant = "default",
  color = "blue",
  className = "",
  disabled = false,
  isLoading = false,
  children = null,
  iconType = "fill",
  iconLeft: IconLeft = undefined,
  iconRight: IconRight = undefined,
  splitDropdown = false,
  dropdownItems = [],
  onClick,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDropdown = dropdownItems && dropdownItems.length > 0;
  const isSplitDropdown = isDropdown && splitDropdown;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDropdown) {
      e.stopPropagation();
      setIsOpen((prev) => {
        const next = !prev;
        if (next) {
          setTimeout(() => ReactTooltip.rebuild(), 0);
        }
        return next;
      });
    }
  };
  const handleSize = () => {
    if (size === "lg" || !size) {
      return children ? "px-4 h-9" : "w-9 h-9";
    } else if (size === "md") {
      return children ? "px-3 h-8" : "w-8 h-8";
    } else if (size === "sm") {
      return children ? "px-3 h-7 text-sm" : "w-7 h-7 text-sm";
    } else if (size === "xs") {
      return children ? "px-2 h-6 text-xs" : "w-6 h-6 text-xs";
    }
  };

  const handleColor = () => {
    if (disabled) {
      return color === "transparent"
        ? "cursor-default border-transparent text-neutral-40"
        : "cursor-default bg-neutral-10 border-neutral-20 text-neutral-40";
    }

    if (color === "transparent") {
      return "text-neutral-50 border-transparent hover:text-neutral-60";
    }

    if (color === "gray") {
      return "text-neutral-50 border-neutral-20 hover:text-neutral-60 hover:border-neutral-50";
    }

    switch (variant) {
      case "outline":
        return clsx(
          "bg-white border-neutral-20",
          color === "blue" &&
            "text-blue hover:text-blue-dark hover:border-blue-dark",
          color === "red" &&
            "text-red hover:text-red-dark hover:border-red-dark",
          color === "green" &&
            "text-green hover:text-green-dark hover:border-green-dark",
          color === "yellow" &&
            "text-yellow hover:text-yellow-dark hover:border-yellow-dark"
        );
      case "link":
        return clsx(
          "border-transparent underline-offset-4 hover:underline",
          color === "blue" && "text-blue hover:text-blue-dark",
          color === "red" && "text-red hover:text-red-dark",
          color === "green" && "text-green hover:text-green-dark",
          color === "yellow" && "text-yellow hover:text-yellow-dark"
        );
      default:
        return clsx(
          "text-white",
          color === "blue" &&
            "border-blue bg-blue hover:border-blue-dark hover:bg-blue-dark",
          color === "red" &&
            "border-red bg-red hover:border-red-dark hover:bg-red-dark",
          color === "green" &&
            "border-green bg-green hover:border-green-dark hover:bg-green-dark",
          color === "yellow" &&
            "border-yellow bg-yellow hover:border-yellow-dark hover:bg-yellow-dark"
        );
    }
  };

  const handleIconSize = () => {
    if (size === "lg") {
      return "16";
    } else if (size === "md") {
      return "14";
    } else if (size === "sm") {
      return "12";
    } else if (size === "xs") {
      return "10";
    }
  };

  const handleIconColor = (specifyType?: "stroke" | "fill") => {
    if (!specifyType) {
      specifyType = iconType;
    }
    if (specifyType === "stroke") {
      if (disabled) {
        return "fill-none stroke-neutral-40";
      }
      if (variant === "outline") {
        return clsx(
          "fill-none",
          color === "blue" && "stroke-blue group-hover:stroke-blue-dark",
          color === "red" && "stroke-red group-hover:stroke-red-dark",
          color === "green" && "stroke-green group-hover:stroke-green-dark",
          color === "yellow" && "stroke-yellow group-hover:stroke-yellow-dark",
          ["gray", "transparent"].includes(color) &&
            "stroke-neutral-50 group-hover:stroke-neutral-60"
        );
      } else {
        return clsx(
          "fill-none",
          ["gray", "transparent"].includes(color)
            ? "stroke-neutral-50 group-hover:stroke-neutral-60"
            : "stroke-white"
        );
      }
    } else {
      if (disabled) {
        return "fill-neutral-40";
      }
      if (variant === "outline" || variant === "link") {
        return clsx(
          color === "blue" && "fill-blue group-hover:fill-blue-dark",
          color === "red" && "fill-red group-hover:fill-red-dark",
          color === "green" && "fill-green group-hover:fill-green-dark",
          color === "yellow" && "fill-yellow group-hover:fill-yellow-dark",
          ["gray", "transparent"].includes(color) &&
            "fill-neutral-50 group-hover:fill-neutral-60"
        );
      } else {
        return clsx(
          ["gray", "transparent"].includes(color)
            ? "fill-neutral-50 group-hover:fill-neutral-60"
            : "fill-white"
        );
      }
    }
  };

  const baseButtonClassName = clsx(
    "inline-flex items-center justify-center border font-medium group whitespace-nowrap",
    handleSize(),
    handleColor()
  );

  const renderButtonContent = () => (
    <>
      {IconLeft && (
        <div>
          {isLoading ? (
            <Icon.Loading
              size={handleIconSize()}
              className={clsx(children && "mr-2", handleIconColor())}
            />
          ) : (
            <IconLeft
              size={handleIconSize()}
              className={clsx(children && "mr-2", handleIconColor())}
            />
          )}
        </div>
      )}
      {isLoading && !IconLeft && !IconRight && (
        <Icon.Loading
          size={handleIconSize()}
          className={clsx(children && "mr-2", handleIconColor())}
        />
      )}
      {children}
      {isDropdown && !isSplitDropdown && (
        <Icon.AngleDown
          size={handleIconSize()}
          iconType="stroke"
          className={clsx(children && "ml-2", handleIconColor("stroke"))}
        />
      )}
      {IconRight && !isDropdown && (
        <div>
          {isLoading ? (
            <Icon.Loading
              size={handleIconSize()}
              className={clsx(children && "ml-2", handleIconColor())}
            />
          ) : (
            <IconRight
              size={handleIconSize()}
              className={clsx(children && "ml-2", handleIconColor())}
            />
          )}
        </div>
      )}
    </>
  );

  const renderDropdownMenu = () =>
    isDropdown &&
    isOpen &&
    dropdownItems.length > 0 && (
      <div
        className={clsx(
          "absolute z-50 mt-1 w-max min-w-full bg-white border border-neutral-20 rounded shadow-lg py-1 top-full",
          isSplitDropdown ? "right-0" : "left-0"
        )}
      >
        {dropdownItems.map((item, index) => {
          const ItemIcon = item.icon;
          return (
            <button
              key={index}
              type="button"
              disabled={item.disabled}
              data-tip={item.tooltip}
              onClick={(e) => {
                e.stopPropagation();
                if (item.disabled) return;
                setIsOpen(false);
                item.onClick();
              }}
              className={clsx(
                "w-full px-4 py-2 text-left flex items-center",
                item.disabled
                  ? "cursor-default text-neutral-40"
                  : "hover:bg-neutral-10"
              )}
            >
              {ItemIcon && (
                <ItemIcon
                  size={handleIconSize()}
                  className={clsx(
                    "mr-2",
                    color === "blue" ? "fill-blue" : "fill-neutral-50"
                  )}
                />
              )}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    );

  if (isSplitDropdown) {
    return (
      <div className="relative inline-flex" ref={dropdownRef}>
        <div className="inline-flex">
          <button
            {...rest}
            type="button"
            className={clsx(
              baseButtonClassName,
              rounded
                ? "rounded-l-full"
                : "rounded-l rounded-r-none border-r-0",
              className
            )}
            disabled={disabled}
            onClick={onClick}
          >
            {renderButtonContent()}
          </button>
          <button
            type="button"
            aria-label="More optimize options"
            className={clsx(
              baseButtonClassName,
              "px-2",
              rounded ? "rounded-r-full" : "rounded-r rounded-l-none",
              disabled && "pointer-events-none"
            )}
            disabled={disabled}
            onClick={toggleDropdown}
          >
            <Icon.AngleDown
              size={handleIconSize()}
              iconType="stroke"
              className={handleIconColor("stroke")}
            />
          </button>
        </div>
        {renderDropdownMenu()}
      </div>
    );
  }

  return (
    <div className="relative inline-flex" ref={dropdownRef}>
      <button
        {...rest}
        type="button"
        className={clsx(
          baseButtonClassName,
          rounded ? "rounded-full" : "rounded",
          className
        )}
        disabled={disabled}
        onClick={isDropdown ? toggleDropdown : onClick}
      >
        {renderButtonContent()}
      </button>

      {renderDropdownMenu()}
    </div>
  );
};

export default Button;
